import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ApiBlueprint, createApiFactory } from '@backstage/frontend-plugin-api';
import {
  configApiRef,
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  githubAuthApiRef,
  identityApiRef,
  storageApiRef,
} from '@backstage/core-plugin-api';
import { signalApiRef } from '@backstage/plugin-signals-react';
import { SignalClient } from '@backstage/plugin-signals';
import {
  ScmAuth,
  ScmIntegrationsApi,
  scmAuthApiRef,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { UserSettingsStorage } from '@backstage/plugin-user-settings';
import { ragAiApiRef, RoadieRagAiClient } from '@internal/plugin-rag-ai';
import { orgDataApiRef, OrgDataClient } from '../apis/OrgDataClient';
import {
  entityTabConfigApiRef,
  EntityTabConfigClient,
} from '../apis/EntityTabConfigClient';
import {
  scoreCardApiRef,
  ScorecardApiClient,
} from '@internal/plugin-score-card';
import {
  devOpsMetricsApiRef,
  DevOpsMetricsClient,
} from '@internal/plugin-devops-metrics';
import { sonarQubeApiRef } from '@backstage-community/plugin-sonarqube-react';
import { SonarQubeClient } from '@backstage-community/plugin-sonarqube';
import {
  testNotificationsApiRef,
  TestNotificationsClient,
} from '../api/TestNotificationsApi';
import { apiProxyApiRef, ApiProxyClient } from '../apis/ApiProxyClient';
import {
  repoDiscoveryApiRef,
  RepoDiscoveryApiClient,
} from '@internal/plugin-gitrepo-discovery';
import {
  goldenPathsApiRef,
  GoldenPathsClient,
} from '../apis/GoldenPathsClient';
import {
  featureFlagsApiRef,
  FeatureFlagsClient,
} from '../apis/FeatureFlagsClient';

const orgDataApi = ApiBlueprint.make({
  name: 'org-data',
  params: define =>
    define(
      createApiFactory({
        api: orgDataApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new OrgDataClient({ discoveryApi, fetchApi }),
      }),
    ),
});

const entityTabConfigApi = ApiBlueprint.make({
  name: 'entity-tab-config',
  params: define =>
    define(
      createApiFactory({
        api: entityTabConfigApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new EntityTabConfigClient({ discoveryApi, fetchApi }),
      }),
    ),
});

const goldenPathsApi = ApiBlueprint.make({
  name: 'golden-paths',
  params: define =>
    define(
      createApiFactory({
        api: goldenPathsApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new GoldenPathsClient({ discoveryApi, fetchApi }),
      }),
    ),
});

const featureFlagsApi = ApiBlueprint.make({
  // Must not be "feature-flags" — that id is owned by @backstage/plugin-app
  // for the core LocalStorageFeatureFlags (apiRef core.featureflags).
  name: 'runtime-feature-flags',
  params: define =>
    define(
      createApiFactory({
        api: featureFlagsApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new FeatureFlagsClient({ discoveryApi, fetchApi }),
      }),
    ),
});

const ragAiApi = ApiBlueprint.make({
  name: 'rag-ai',
  params: define =>
    define(
      createApiFactory({
        api: ragAiApiRef,
        deps: {
          configApi: configApiRef,
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
          identityApi: identityApiRef,
        },
        factory: ({ configApi, discoveryApi, fetchApi, identityApi }) =>
          new RoadieRagAiClient({
            configApi,
            discoveryApi,
            fetchApi,
            identityApi,
          }),
      }),
    ),
});

const apiProxyApi = ApiBlueprint.make({
  name: 'api-proxy',
  params: define =>
    define(
      createApiFactory({
        api: apiProxyApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new ApiProxyClient({ discoveryApi, fetchApi }),
      }),
    ),
});

const scoreCardApi = ApiBlueprint.make({
  name: 'score-card',
  params: define =>
    define(
      createApiFactory({
        api: scoreCardApiRef,
        deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
        factory: ({ discoveryApi, identityApi }) =>
          new ScorecardApiClient(discoveryApi, identityApi),
      }),
    ),
});

const repoDiscoveryApi = ApiBlueprint.make({
  name: 'repo-discovery',
  params: define =>
    define(
      createApiFactory({
        api: repoDiscoveryApiRef,
        deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
        factory: ({ discoveryApi, identityApi }) =>
          new RepoDiscoveryApiClient(discoveryApi, identityApi),
      }),
    ),
});

const devOpsMetricsApi = ApiBlueprint.make({
  name: 'devops-metrics',
  params: define =>
    define(
      createApiFactory({
        api: devOpsMetricsApiRef,
        deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
        factory: ({ discoveryApi, identityApi }) =>
          new DevOpsMetricsClient({ discoveryApi, identityApi }),
      }),
    ),
});

const sonarQubeApi = ApiBlueprint.make({
  name: 'sonarqube',
  params: define =>
    define(
      createApiFactory({
        api: sonarQubeApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) => {
          return new SonarQubeClient({ discoveryApi, fetchApi }) as any;
        },
      }),
    ),
});

const scmIntegrationsApi = ApiBlueprint.make({
  name: 'scm-integrations',
  params: define =>
    define(
      createApiFactory({
        api: scmIntegrationsApiRef,
        deps: { configApi: configApiRef },
        factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
      }),
    ),
});

const scmAuthApi = ApiBlueprint.make({
  name: 'scm-auth',
  params: define =>
    define(
      createApiFactory({
        api: scmAuthApiRef,
        deps: { githubAuthApi: githubAuthApiRef },
        factory: ({ githubAuthApi }) => ScmAuth.forGithub(githubAuthApi),
      }),
    ),
});

const testNotificationsApi = ApiBlueprint.make({
  name: 'test-notifications',
  params: define =>
    define(
      createApiFactory({
        api: testNotificationsApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new TestNotificationsClient({ discoveryApi, fetchApi }),
      }),
    ),
});

const storageApi = ApiBlueprint.make({
  name: 'storage',
  params: define =>
    define(
      createApiFactory({
        api: storageApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          errorApi: errorApiRef,
          fetchApi: fetchApiRef,
          identityApi: identityApiRef,
        },
        factory: deps => UserSettingsStorage.create(deps),
      }),
    ),
});

const signalsApi = ApiBlueprint.make({
  name: 'signals',
  params: define =>
    define(
      createApiFactory({
        api: signalApiRef,
        deps: {
          identityApi: identityApiRef,
          discoveryApi: discoveryApiRef,
        },
        factory: ({ identityApi, discoveryApi }) =>
          SignalClient.create({ identity: identityApi, discoveryApi }),
      }),
    ),
});

export const apisModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    orgDataApi,
    entityTabConfigApi,
    goldenPathsApi,
    featureFlagsApi,
    ragAiApi,
    apiProxyApi,
    scoreCardApi,
    repoDiscoveryApi,
    devOpsMetricsApi,
    sonarQubeApi,
    scmIntegrationsApi,
    scmAuthApi,
    testNotificationsApi,
    storageApi,
    signalsApi,
  ],
});
